import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Collapse from "@mui/material/Collapse"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import MenuItem from "@mui/material/MenuItem"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Select from "@mui/material/Select"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import {
  TemplateGroupTabs,
  useTemplatePickerTab,
} from "../components/templateGroups"
import { AMD_MACHINE_ACTIONS, AMD_MODES, amdFormBlocker } from "./amd"

function normalizeTemplateList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

function mergeTemplates(inboxTemplates, campaigns) {
  const byId = new Map()
  ;[...inboxTemplates, ...campaigns].forEach((item) => {
    if (item?.id != null) {
      byId.set(item.id, { ...item, label: item.name || `Template ${item.id}` })
    }
  })
  return [...byId.values()]
}

/**
 * Answering-machine detection choices for a Power Dialer session, shown in the
 * start-session dialog. `value`/`onChange` hold the form shape from
 * `amdFormFromOptions`.
 */
export default function AmdSessionOptionsForm({
  options,
  value,
  onChange,
  disabled = false,
}) {
  const axios = useAuthorizedAxiosClient()
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const recordings = options?.voicemail_recordings || []
  const needsTemplates = value?.enabled && value?.machine_action === "text"

  useEffect(() => {
    if (!needsTemplates || templates.length) {
      return undefined
    }
    let cancelled = false
    setTemplatesLoading(true)
    Promise.all([
      axios
        .get("/crm/api/concierge-sms/inbox/templates/")
        .then(({ data }) => normalizeTemplateList(data))
        .catch(() => []),
      axios
        .get("/workflows/api/sms/select-campaign/")
        .then(({ data }) => normalizeTemplateList(data))
        .catch(() => []),
    ])
      .then(([inboxTemplates, campaigns]) => {
        if (!cancelled) setTemplates(mergeTemplates(inboxTemplates, campaigns))
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [axios, needsTemplates, templates.length])

  function patch(changes) {
    onChange({ ...value, ...changes })
  }

  const blocker = amdFormBlocker(value, options)
  const selectedTemplate =
    templates.find((item) => item.id === value?.sms_campaign_id) || null
  const {
    tab: templateTab,
    setTab: setTemplateTab,
    options: templateOptions,
  } = useTemplatePickerTab(templates)

  return (
    <Column
      gap={1}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: value?.enabled ? "colors.brand200" : "colors.gray200",
        bgcolor: value?.enabled ? "colors.brand50" : "transparent",
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value?.enabled)}
            disabled={disabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
        }
        label={
          <Column>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Detect answering machines
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Skip the voicemail greeting so you only talk to people.
            </Typography>
          </Column>
        }
      />

      <Collapse in={Boolean(value?.enabled)} unmountOnExit>
        <Column gap={1.5} sx={{ pt: 1 }}>
          <FormControl disabled={disabled}>
            <FormLabel sx={{ fontWeight: 700, fontSize: 14 }}>
              When voicemail picks up
            </FormLabel>
            <RadioGroup
              value={value?.machine_action || "text"}
              onChange={(e) => patch({ machine_action: e.target.value })}
            >
              {AMD_MACHINE_ACTIONS.map((action) => (
                <FormControlLabel
                  key={action.value}
                  value={action.value}
                  control={<Radio size="small" />}
                  label={
                    <Column>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {action.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {action.help}
                      </Typography>
                    </Column>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {value?.machine_action === "text" ? (
            <Column sx={{ gap: 0.75 }}>
              <TemplateGroupTabs tab={templateTab} onChange={setTemplateTab} />
              <Autocomplete
                value={selectedTemplate}
                onChange={(_, option) =>
                  patch({ sms_campaign_id: option?.id ?? null })
                }
                options={templateOptions}
                loading={templatesLoading}
                disabled={disabled}
                getOptionLabel={(option) => option.label || option.name || ""}
                isOptionEqualToValue={(option, selected) =>
                  option.id === selected.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Text to send"
                    placeholder="Select an SMS template"
                  />
                )}
                noOptionsText={
                  templatesLoading
                    ? "Loading templates…"
                    : "No SMS templates found"
                }
              />
            </Column>
          ) : null}

          {value?.machine_action === "voicemail" ? (
            <FormControl size="small" fullWidth disabled={disabled}>
              <FormLabel sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>
                Voicemail to play
              </FormLabel>
              <Select
                displayEmpty
                value={value?.voicemail_recording_id ?? ""}
                onChange={(e) =>
                  patch({ voicemail_recording_id: e.target.value || null })
                }
              >
                <MenuItem value="">
                  <em>Choose a recording</em>
                </MenuItem>
                {recordings.map((recording) => (
                  <MenuItem key={recording.id} value={recording.id}>
                    {recording.name}
                  </MenuItem>
                ))}
              </Select>
              {recordings.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Record one in Settings → Phone / SMS first.
                </Typography>
              ) : null}
            </FormControl>
          ) : null}

          <FormControl disabled={disabled}>
            <FormLabel sx={{ fontWeight: 700, fontSize: 14 }}>
              Detection style
            </FormLabel>
            <RadioGroup
              value={value?.mode || "listen_in"}
              onChange={(e) => patch({ mode: e.target.value })}
            >
              {AMD_MODES.map((mode) => (
                <FormControlLabel
                  key={mode.value}
                  value={mode.value}
                  control={<Radio size="small" />}
                  label={
                    <Column>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {mode.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {mode.help}
                      </Typography>
                    </Column>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {blocker ? <Alert severity="warning">{blocker}</Alert> : null}
        </Column>
      </Collapse>
    </Column>
  )
}
